import { useEffect } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { ChevronRight, ArrowUpRight, Phone, Mail, Globe, MessageCircle, Smartphone, CheckCircle2,
    Home, Building2, Factory, Warehouse, Compass, Radar, Map, PenTool, Sofa, KeyRound, ClipboardList, Landmark, Package } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { VideoPlayer } from "@/components/VideoPlayer";
import { findService, SERVICE_PAGES } from "@/data/services";
import { CONTACT } from "@/data/content";
import { trackEvent, EVENTS } from "@/lib/analytics";

const ICONS = { Home, Building2, Factory, Warehouse, Compass, Radar, Map, PenTool, Sofa, KeyRound, ClipboardList, Landmark, Package };

export default function ServiceDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const service = findService(slug);

    useEffect(() => { window.scrollTo(0, 0); }, [slug]);

    if (!service) return <Navigate to="/" replace />;
    const Icon = ICONS[service.icon] || Package;
    const related = service.related.map(findService).filter(Boolean);

    const discuss = () => { trackEvent(EVENTS.estimateClick, { location: `service:${slug}` }); navigate("/", { state: { scrollTo: "#contact" } }); };

    return (
        <div className="bg-omsivo-paper">
            <Navbar />
            <section className="blueprint-fine noise relative overflow-hidden pb-20 pt-28 lg:pt-36" data-testid="service-hero">
                <div className="relative z-10 mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                    <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45" aria-label="Breadcrumb" data-testid="service-breadcrumb">
                        <Link to="/" className="transition-colors hover:text-white">Home</Link>
                        <ChevronRight className="h-3 w-3" />
                        <Link to="/#services" className="transition-colors hover:text-white">Services</Link>
                        <ChevronRight className="h-3 w-3" />
                        <span className="text-flame">{service.name}</span>
                    </nav>
                    <div className="mt-10 grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-7">
                            <Reveal y={16}>
                                <span className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.26em] text-white/65 backdrop-blur">
                                    <Icon className="h-4 w-4 text-flame" /> OMSIVO Service
                                </span>
                            </Reveal>
                            <MaskedLines
                                className="mt-6 font-display font-black uppercase leading-[1.0] tracking-tight"
                                lineClassName="text-4xl text-white sm:text-5xl lg:text-6xl"
                                lines={[service.name]}
                            />
                            <Reveal delay={0.4}>
                                <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">{service.short}</p>
                            </Reveal>
                            <Reveal delay={0.5}>
                                <div className="mt-9 flex flex-wrap items-center gap-4">
                                    <button onClick={discuss} data-testid="service-discuss-btn"
                                        className="rounded-full bg-flame px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.1em] text-white shadow-xl shadow-flame/25 transition-transform hover:-translate-y-0.5">
                                        Discuss Your Project
                                    </button>
                                    <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="service-whatsapp-btn"
                                        onClick={() => trackEvent(EVENTS.whatsappClick, { location: `service:${slug}` })}
                                        className="flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.1em] text-white transition-colors hover:border-flame hover:text-flame">
                                        <MessageCircle className="h-4 w-4 text-flame" /> WhatsApp
                                    </a>
                                </div>
                            </Reveal>
                        </div>
                        <Reveal className="lg:col-span-5" delay={0.2}>
                            <div className="spotlight-frame overflow-hidden rounded-[28px] border border-white/10 shadow-2xl shadow-black/50"
                                style={{ clipPath: "polygon(0 0, 100% 0, 100% 90%, 90% 100%, 0 100%)" }}>
                                <img src={service.image} alt={`${service.name} — concept visualization`} className="h-[300px] w-full object-cover lg:h-[380px]" data-testid="service-hero-image" />
                            </div>
                            <p className="mt-3 text-right font-mono text-[8px] uppercase tracking-[0.22em] text-white/35">Concept visualization</p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28" data-testid="service-details">
                <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-7">
                            <Reveal><h2 className="font-display text-2xl font-extrabold uppercase tracking-wide text-omsivo-navy sm:text-3xl">What we do</h2></Reveal>
                            <div className="mt-7 space-y-3">
                                {service.whatWeDo.map((w, i) => (
                                    <Reveal key={w} delay={0.05 * i}>
                                        <div className="flex items-start gap-4 rounded-2xl border border-omsivo-navy/10 bg-white p-5" data-testid={`service-what-${i}`}>
                                            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-flame-dark" />
                                            <p className="text-[15px] leading-relaxed text-slate-700">{w}</p>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                            <Reveal delay={0.1}>
                                <h2 className="mt-14 font-display text-2xl font-extrabold uppercase tracking-wide text-omsivo-navy sm:text-3xl">How the process works</h2>
                            </Reveal>
                            <div className="mt-7 space-y-0 rounded-2xl border border-omsivo-navy/10 bg-white">
                                {service.process.map((p, i) => (
                                    <div key={p} className="flex items-center gap-5 border-b border-omsivo-navy/8 px-6 py-4.5 py-4 last:border-0" data-testid={`service-process-${i}`}>
                                        <span className="font-display text-xl font-black text-flame-dark">{String(i + 1).padStart(2, "0")}</span>
                                        <p className="text-sm font-medium text-slate-700">{p}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <Reveal><h2 className="font-display text-2xl font-extrabold uppercase tracking-wide text-omsivo-navy sm:text-3xl">Who it is for</h2></Reveal>
                            <div className="mt-7 space-y-3">
                                {service.whoFor.map((w, i) => (
                                    <Reveal key={w} delay={0.05 * i}>
                                        <div className="rounded-2xl border border-omsivo-navy/10 bg-omsivo-navy p-5" data-testid={`service-who-${i}`}>
                                            <p className="text-sm font-medium leading-relaxed text-white/85">{w}</p>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                            <Reveal delay={0.1}>
                                <h2 className="mt-12 font-display text-lg font-extrabold uppercase tracking-wide text-omsivo-navy">Key service areas</h2>
                                <div className="mt-5 flex flex-wrap gap-2" data-testid="service-key-areas">
                                    {service.keyAreas.map((k) => (
                                        <span key={k} className="rounded-full border border-omsivo-navy/12 bg-white px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-omsivo-slate shadow-sm">{k}</span>
                                    ))}
                                </div>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <div className="mt-12" data-testid="service-video">
                                    <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-omsivo-navy">Watch the concept</h2>
                                    <VideoPlayer className="mt-5 rounded-2xl border border-omsivo-navy/10" src={null} label={`${service.name} concept video`} testid="service-video-player" />
                                    <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">Concept video coming soon</p>
                                </div>
                            </Reveal>
                        </div>
                    </div>

                    {service.materials && (
                        <Reveal>
                            <div className="blueprint mt-16 rounded-[28px] p-8 lg:p-12" data-testid="materials-app-block">
                                <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
                                    <div>
                                        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-flame">Building Materials · Digital Journey</p>
                                        <h3 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">
                                            Order building materials<br />through the OMSIVO App.
                                        </h3>
                                        <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/65">
                                            Discover, select, compare, buy and track delivery — the material marketplace is being built into the OMSIVO Customer App.
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-start gap-3">
                                        <button disabled data-testid="materials-app-btn"
                                            className="flex cursor-not-allowed items-center gap-3 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white/50">
                                            <Smartphone className="h-5 w-5" /> OMSIVO App
                                        </button>
                                        <span className="rounded-full bg-flame px-4 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.24em] text-white">Coming Soon</span>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    )}

                    <div className="mt-20">
                        <Reveal><h2 className="font-display text-2xl font-extrabold uppercase tracking-wide text-omsivo-navy sm:text-3xl">Related services</h2></Reveal>
                        <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
                            {related.map((r) => (
                                <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`service-related-${r.slug}`}
                                    className="lift-card group rounded-2xl border border-omsivo-navy/10 bg-white p-6">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-display text-base font-extrabold uppercase tracking-[0.05em] text-omsivo-navy">{r.name}</h3>
                                        <ArrowUpRight className="h-4 w-4 text-slate-300 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-flame" />
                                    </div>
                                    <p className="mt-2 line-clamp-2 text-[13px] leading-relaxed text-slate-600">{r.short}</p>
                                </Link>
                            ))}
                        </div>
                    </div>

                    <Reveal>
                        <div className="mt-16 grid grid-cols-1 gap-3 rounded-[28px] bg-omsivo-navy p-8 sm:grid-cols-3 lg:p-10" data-testid="service-contact-strip">
                            <a href={CONTACT.phoneTel} className="flex items-center gap-3 text-white/85 transition-colors hover:text-flame" data-testid="service-phone">
                                <Phone className="h-4 w-4 shrink-0 text-flame" /> <span className="font-display font-bold tracking-wider">{CONTACT.phoneDisplay}</span>
                            </a>
                            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-white/85 transition-colors hover:text-flame" data-testid="service-email">
                                <Mail className="h-4 w-4 shrink-0 text-flame" /> {CONTACT.email}
                            </a>
                            <span className="flex items-center gap-3 text-white/85" data-testid="service-website">
                                <Globe className="h-4 w-4 shrink-0 text-flame" /> {CONTACT.website}
                            </span>
                        </div>
                    </Reveal>
                </div>
            </section>
            <Footer />
        </div>
    );
}

export { SERVICE_PAGES };
